<template>
  <div>
    <v-card flat>
      <v-card-title class="text-h5 font-weight-bold">
        {{ $t('About Page') }}
      </v-card-title>

      <v-card-text>
        <v-form ref="form">
          <v-row>
            <v-col cols="12">
              <v-card outlined class="mb-4">
                <v-card-text class="d-flex align-center">
                  <div class="text-subtitle-1 font-weight-medium">
                    {{ $t('Page Status') }}
                  </div>
                  <v-spacer></v-spacer>
                  <v-switch
                    v-model="formData.pages.about.active"
                    :label="
                      formData.pages.about.active
                        ? $t('Active')
                        : $t('Inactive')
                    "
                    dense
                    hide-details
                    class="mt-0"
                    @change="updateForm"
                  ></v-switch>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-expansion-panels v-model="openPanel">
                <!-- Header Section Panel -->
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $t('Header Content') }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-text-field
                      v-model="formData.pages.about.header.title"
                      dense
                      outlined
                      :rules="[required]"
                      :label="$t('Title')"
                      @input="updateForm"
                    />

                    <v-text-field
                      v-model="formData.pages.about.header.sub_title"
                      dense
                      outlined
                      :label="$t('Subtitle')"
                      @input="updateForm"
                    />

                    <v-textarea
                      v-model="formData.pages.about.header.story"
                      outlined
                      :label="$t('Our Story')"
                      :rules="[required]"
                      @input="updateForm"
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Header Image Panel -->
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $t('Header Image') }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12">
                        <v-input
                          :value="formData.pages.about.header_image"
                          :rules="[required]"
                        />
                        <gallery
                          :key="formData.pages.about.header_image"
                          button-text="Header image"
                          folder="booking-engine"
                          :compress="false"
                          :cols="6"
                          :thumbnails="true"
                          readonly
                          :images="
                            formData.pages.about.header_image
                              ? [formData.pages.about.header_image]
                              : []
                          "
                          :on-change="afterHeaderImageUpload"
                          :uploadable="true"
                        />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Team Members Panel -->
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    {{ $t('Team Members') }}
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-btn
                      color="primary"
                      small
                      class="mb-4"
                      @click="addTeamMember"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      {{ $t('Add Team Member') }}
                    </v-btn>

                    <v-row>
                      <v-col
                        v-for="(member, index) in formData.pages.about
                          .team_members"
                        :key="index"
                        cols="12"
                      >
                        <v-card outlined class="pa-4">
                          <v-row>
                            <v-col cols="12" md="8">
                              <v-text-field
                                v-model="member.name"
                                dense
                                outlined
                                :rules="[required]"
                                :label="$t('Name')"
                                @input="updateForm"
                              />

                              <v-text-field
                                v-model="member.position"
                                dense
                                outlined
                                :rules="[required]"
                                :label="$t('Position')"
                                @input="updateForm"
                              />

                              <v-textarea
                                v-model="member.bio"
                                outlined
                                :label="$t('Bio')"
                                @input="updateForm"
                              />
                            </v-col>

                            <v-col cols="12" md="4">
                              <gallery
                                :key="member.image"
                                button-text="Profile Photo"
                                folder="booking-engine"
                                :compress="false"
                                :cols="12"
                                :thumbnails="true"
                                readonly
                                :images="member.image ? [member.image] : []"
                                :on-change="
                                  imgs => afterMemberImageUpload(imgs, index)
                                "
                                :uploadable="true"
                              />

                              <v-btn
                                color="error"
                                text
                                block
                                class="mt-4"
                                @click="removeTeamMember(index)"
                              >
                                <v-icon left small>mdi-delete</v-icon>
                                {{ $t('Remove') }}
                              </v-btn>
                            </v-col>
                          </v-row>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>

            <v-col cols="12" md="6">
              <preview-frame
                :url="`${previewUrl}/about`"
                :needs-refresh="needsRefresh"
                @refresh="refreshPreview"
                @load="onFrameLoad"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import FormRulesMixin from 'components/mixins/form-rules-mixin'
import Gallery from 'components/common/gallery.vue'
import PreviewFrame from '../components/preview-frame.vue'
import axios from 'axios'

export default {
  name: 'AboutPage',

  components: {
    Gallery,
    PreviewFrame,
  },

  mixins: [FormRulesMixin],

  props: {
    bookingEngineSetting: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      needsRefresh: false,
      isFrameLoading: true,
      openPanel: 0,
      formData: {
        name: this.bookingEngineSetting.name,
        pages: {
          about: {
            active: this.bookingEngineSetting.pages?.about?.active || false,
            header: {
              title:
                this.bookingEngineSetting.pages?.about?.header?.title || '',
              sub_title:
                this.bookingEngineSetting.pages?.about?.header?.sub_title || '',
              story:
                this.bookingEngineSetting.pages?.about?.header?.story || '',
            },
            header_image:
              this.bookingEngineSetting.pages?.about?.header_image || null,
            team_members:
              this.bookingEngineSetting.pages?.about?.team_members || [],
          },
        },
      },
    }
  },

  computed: {
    previewUrl() {
      return process.env.NODE_ENV === 'development'
        ? 'http://localhost:8081'
        : `https://${this.bookingEngineSetting.name}.bookingsboom.com`
    },
  },

  watch: {
    'formData.pages.about.active': {
      handler(newVal) {
        // Refresh preview when active state changes
        this.refreshPreview()
      },
    },
    bookingEngineSetting: {
      handler(newVal) {
        // Update formData when bookingEngineSetting changes
        this.formData = {
          name: newVal.name,
          pages: {
            about: {
              active: newVal.pages?.about?.active || false,
              header: {
                title: newVal.pages?.about?.header?.title || '',
                sub_title: newVal.pages?.about?.header?.sub_title || '',
                story: newVal.pages?.about?.header?.story || '',
              },
              header_image: newVal.pages?.about?.header_image || null,
              team_members: newVal.pages?.about?.team_members || [],
            },
          },
        }
      },
      deep: true,
    },
  },

  methods: {
    updateForm() {
      // Skip validation if form ref is not available yet
      if (!this.$refs.form || this.$refs.form.validate()) {
        this.$emit('update:setting', {
          ...this.bookingEngineSetting,
          pages: {
            ...this.bookingEngineSetting.pages,
            about: this.formData.pages.about,
          },
        })
        this.needsRefresh = true
      }
    },

    addTeamMember() {
      this.formData.pages.about.team_members.push({
        name: '',
        position: '',
        bio: '',
        image: null,
      })
      this.updateForm()
    },

    removeTeamMember(index) {
      this.formData.pages.about.team_members.splice(index, 1)
      this.updateForm()
    },

    afterHeaderImageUpload(imgs) {
      this.formData.pages.about.header_image = imgs[0]
      this.updateForm()
    },

    afterMemberImageUpload(imgs, index) {
      this.formData.pages.about.team_members[index].image = imgs[0]
      this.$forceUpdate()
      this.updateForm()
    },

    validate() {
      return this.$refs.form.validate()
    },

    async refreshPreview() {
      // Skip validation if form ref is not available yet
      if (!this.$refs.form || this.$refs.form.validate()) {
        try {
          this.isFrameLoading = true

          // Save changes to backend
          await axios.patch(
            `/api/booking-engine-settings/${this.bookingEngineSetting.id}`,
            {
              name: this.bookingEngineSetting.name,
              pages: {
                ...this.bookingEngineSetting.pages,
                about: this.formData.pages.about,
              },
            }
          )

          this.needsRefresh = false
        } catch (error) {
          console.error('Failed to save settings:', error)
          this.$store.dispatch('snackbar/showSnackbar', {
            message: this.$t('Failed to save settings'),
            color: 'error',
          })
        }
      }
    },

    onFrameLoad() {
      this.isFrameLoading = false
    },
  },
}
</script>

<style scoped>
.preview-container {
  position: relative;
}

.preview-frame-container {
  position: relative;
}

iframe {
  border: none;
}
</style>
